import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { solutionKeys } from "@/lib/solution-keys";
import type { VersionDetail } from "./use-solutions";

export interface VersionListRow {
  id: string;
  solutionId: string;
  versionNumber: number;
  label: string | null;
  isCurrent: boolean;
  uploadedBy: string;
  uploadedByName: string;
  commitSha: string | null;
  changeSummary: string | null;
  createdAt: string;
  commentCount: number;
  unresolvedCount: number;
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) {
    let message = `${res.status}`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export function useVersions(slug: string) {
  return useQuery({
    queryKey: solutionKeys.versions(slug),
    enabled: Boolean(slug),
    queryFn: async (): Promise<VersionListRow[]> => {
      const json = await api<{ data: VersionListRow[] }>(`/api/solutions/${slug}/versions`);
      return json.data;
    },
  });
}

export function useVersion(slug: string, versionId: string | undefined | null) {
  return useQuery({
    queryKey: solutionKeys.version(slug, versionId ?? "none"),
    enabled: Boolean(slug && versionId),
    queryFn: async (): Promise<VersionDetail> => {
      const json = await api<{ data: VersionDetail }>(
        `/api/solutions/${slug}/versions/${versionId}`,
      );
      return json.data;
    },
  });
}

export function useUploadVersion(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      sourceMd?: string;
      file?: File;
      label?: string;
      changeSummary?: string;
      makeCurrent?: boolean;
    }) => {
      let res: Response;
      if (input.file) {
        const form = new FormData();
        form.append("file", input.file);
        if (input.label) form.append("label", input.label);
        if (input.changeSummary) form.append("changeSummary", input.changeSummary);
        if (input.makeCurrent === false) form.append("makeCurrent", "false");
        res = await fetch(`/api/solutions/${slug}/versions`, { method: "POST", body: form });
      } else {
        res = await fetch(`/api/solutions/${slug}/versions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sourceMd: input.sourceMd,
            label: input.label,
            changeSummary: input.changeSummary,
            makeCurrent: input.makeCurrent,
          }),
        });
      }
      if (!res.ok) {
        let message = `${res.status}`;
        try {
          const body = await res.json();
          if (body?.error) message = body.error;
        } catch {
          // ignore
        }
        throw new Error(message);
      }
      const json = (await res.json()) as { data: VersionDetail };
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionKeys.all });
      qc.invalidateQueries({ queryKey: solutionKeys.versions(slug) });
      qc.invalidateQueries({ queryKey: solutionKeys.detail(slug) });
      toast.success("Version uploaded");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Upload failed");
    },
  });
}

export function useSetCurrentVersion(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (versionId: string) => {
      const json = await api<{ data: VersionDetail }>(
        `/api/solutions/${slug}/versions/${versionId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ isCurrent: true }),
        },
      );
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionKeys.versions(slug) });
      qc.invalidateQueries({ queryKey: solutionKeys.detail(slug) });
      toast.success("Current version updated");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to set current");
    },
  });
}

export function useUpdateVersionLabel(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { versionId: string; label: string | null }) => {
      const json = await api<{ data: VersionDetail }>(
        `/api/solutions/${slug}/versions/${input.versionId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ label: input.label }),
        },
      );
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionKeys.versions(slug) });
    },
  });
}
